import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import format from "date-fns/format";
import { ComponentType, useMemo } from "react";
import { useDispatch } from "react-redux";
import { useLocation, Link } from "react-router-dom";

import { openAdminDocument } from "shared/actions/admin/adminDocumentsActions";
import { printDocumentsLink } from "shared/utils/printUtils";

import { PathologyWatchButton } from "components/customInputs/buttons/Button";

import { extractBasePathname } from "../adminUtils";

const DocumentsColumns =
  <P extends object>(
    Component: ComponentType<P>,
    options?: {
      displayCaseInfo?: boolean;
      showPrintLabels?: boolean;
    }
  ) =>
  (props: any) => {
    const dispatch = useDispatch();
    const location = useLocation();
    const columns = useMemo(
      () => [
        ...(options?.displayCaseInfo
          ? [
              {
                Header: "Case Accession",
                accessor: "case__accession",
              },
            ]
          : []),
        {
          Header: "Name",
          accessor: "name",
        },
        {
          Header: "Category",
          accessor: "category",
          Cell: ({ row }: any) => (
            <>{row.original.category?.replaceAll("-", " ")}</>
          ),
        },
        {
          Header: "Created At",
          accessor: "created_at",
          Cell: ({ row }: any) => (
            <>
              {row.original.created_at &&
                format(new Date(row.original.created_at), "MM/dd/yyyy")}
            </>
          ),
        },
        {
          Header: "Status",
          accessor: "status",
        },
        {
          Header: "Actions",
          Cell: ({ row }: any) => {
            const editPathname = location.pathname.includes("/documents")
              ? `${extractBasePathname(location.pathname)}${row.original.id}`
              : `${extractBasePathname(location.pathname)}documents/${
                  row.original.id
                }`;
            return (
              <div
                className="cell__content--horizontal"
                style={{ gap: "0.5em", flexWrap: "nowrap" }}
              >
                {props.adminCase?.id && options?.showPrintLabels && (
                  <Link
                    to={printDocumentsLink(props.adminCase?.id, row.original)}
                    target="_blank"
                    rel="opener"
                    className="PWTable__actions__link cell__content--noWrap"
                  >
                    Print "{row.original.category}
                    " Label <FontAwesomeIcon icon="print" />
                  </Link>
                )}
                <Link to={editPathname}>
                  <PathologyWatchButton size="small">Edit</PathologyWatchButton>
                </Link>
                {props.setPreviewDocumentId && (
                  <PathologyWatchButton
                    onClick={() => {
                      row.original.id &&
                        props.setPreviewDocumentId(row.original.id);
                    }}
                    size="small"
                  >
                    Preview
                  </PathologyWatchButton>
                )}
                <PathologyWatchButton
                  onClick={() => {
                    row.original.id &&
                      dispatch(openAdminDocument(row.original.id));
                  }}
                  size="small"
                >
                  View&nbsp;&rarr;
                </PathologyWatchButton>
                {options?.displayCaseInfo && row.original.case_id && (
                  <Link
                    to={`/admin/cases/${row.original.case_id}`}
                    state={{
                      label: "Documents",
                      prevUrl: `${location.pathname}${location.search}`,
                    }}
                  >
                    <PathologyWatchButton size="small">
                      Go to Case&nbsp;&rarr;
                    </PathologyWatchButton>
                  </Link>
                )}
              </div>
            );
          },
        },
      ],
      [location]
    );

    return <Component {...props} columns={columns} />;
  };

export default DocumentsColumns;
